import type { ID } from './common.types';

export interface AuthUser {
  id: ID;
  email: string;
  firstName: string;
  lastName: string;
  avatar?: string;
  employeeRole: string;
  permissions: string[];
  organizationId: ID;
  isEmailVerified: boolean;
}

export interface LoginPayload {
  email: string;
  password: string;
  rememberMe?: boolean;
}

export interface RegisterPayload {
  firstName: string;
  lastName: string;
  middleName?: string;
  email: string;
  country?: string;
  phoneNumber?: string;
  password: string;
}

export interface ForgotPasswordPayload {
  username: string;
}

export interface ResetPasswordPayload {
  token: string;
  password: string;
  confirmPassword: string;
}

export interface AuthTokens {
  accessToken: string;
  refreshToken: string;
  expiresIn: number;
}

export interface AuthResponse {
  user: AuthUser;
  tokens: AuthTokens;
}

export type AuthState = {
  user: AuthUser | null;
  tokens: AuthTokens | null;
  isAuthenticated: boolean;
  isLoading: boolean;
};

// ─── New V2 Login Types ──────────────────────────────────────────────────────

/** Payload for POST /v2/users/login */
export interface V2LoginPayload {
  username: string;
  password: string;
}

/** Response shape from POST /v2/users/login */
export interface V2LoginResponse {
  id: string;
  first_name: string;
  last_name: string;
  user_type: string;
  access_token: string;
  refresh_token: string;
  account_status: string;
  session_id: string;
  access_token_expires_at: string;
}

// ─── User Profile from /v2/users/me ──────────────────────────────────────────

/** Response shape from GET /v2/users/me */
export interface UserProfile {
  id: string;
  firstname: string;
  lastname: string;
  middlename: string;
  primaryEmail: string;
  primaryPhone: string;
  emailVerified: boolean;
  phoneVerified: boolean;
  accountStatus: string;
  createdAt: string;
  updatedAt: string;
  country: string;
  authType: string;
  partnerId: string;
  area: string;
  territories: string;
  roles: string[];
  deviceTokens: string[];
}

// ─── User Authority from /v2/users/authority ─────────────────────────────────

/** Response shape from GET /v2/users/authority */
export interface UserAuthority {
  roles: string[];
  permissions: string[];
}

// ─── TalentChek Role Constants ───────────────────────────────────────────────

export enum TalentChekRole {
  STAFF = 'TALENTCHEK_STAFF',
  GLOBAL_HR = 'TALENTCHEK_GLOBAL_HR',
  COUNTRY_HR = 'TALENTCHEK_COUNTRY_HR',
  ADMIN = 'TALENTCHEK_ADMIN',
  COUNTRY_MANAGER = 'TALENTCHEK_COUNTRY_MANAGER',
}
